// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  Image,
  ImagesListOptionalParams,
  ImagesListByResourceGroupOptionalParams,
  ImagesGetOptionalParams,
  ImagesGetResponse,
  ImagesCreateOrUpdateOptionalParams,
  ImagesCreateOrUpdateResponse,
  ImageUpdate,
  ImagesUpdateOptionalParams,
  ImagesUpdateResponse,
  ImagesDeleteOptionalParams,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a Images. */
export interface Images {
  /**
   * Gets the list of Images in the subscription. Use nextLink property in the response to get the next
   * page of Images. Do this till nextLink is null to fetch all the Images.
   * @param options The options parameters.
   */
  list(options?: ImagesListOptionalParams): PagedAsyncIterableIterator<Image>;
  /**
   * Gets the list of images under a resource group. Use nextLink property in the response to get the
   * next page of Images. Do this till nextLink is null to fetch all the Images.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  listByResourceGroup(
    resourceGroupName: string,
    options?: ImagesListByResourceGroupOptionalParams,
  ): PagedAsyncIterableIterator<Image>;
  /**
   * Gets an image.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param imageName The name of the image.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    imageName: string,
    options?: ImagesGetOptionalParams,
  ): Promise<ImagesGetResponse>;
  /**
   * Create or update an image.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param imageName The name of the image.
   * @param parameters Parameters supplied to the Create Image operation.
   * @param options The options parameters.
   */
  beginCreateOrUpdate(
    resourceGroupName: string,
    imageName: string,
    parameters: Image,
    options?: ImagesCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<OperationState<ImagesCreateOrUpdateResponse>, ImagesCreateOrUpdateResponse>
  >;
  /**
   * Create or update an image.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param imageName The name of the image.
   * @param parameters Parameters supplied to the Create Image operation.
   * @param options The options parameters.
   */
  beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    imageName: string,
    parameters: Image,
    options?: ImagesCreateOrUpdateOptionalParams,
  ): Promise<ImagesCreateOrUpdateResponse>;
  /**
   * Update an image.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param imageName The name of the image.
   * @param parameters Parameters supplied to the Update Image operation.
   * @param options The options parameters.
   */
  beginUpdate(
    resourceGroupName: string,
    imageName: string,
    parameters: ImageUpdate,
    options?: ImagesUpdateOptionalParams,
  ): Promise<SimplePollerLike<OperationState<ImagesUpdateResponse>, ImagesUpdateResponse>>;
  /**
   * Update an image.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param imageName The name of the image.
   * @param parameters Parameters supplied to the Update Image operation.
   * @param options The options parameters.
   */
  beginUpdateAndWait(
    resourceGroupName: string,
    imageName: string,
    parameters: ImageUpdate,
    options?: ImagesUpdateOptionalParams,
  ): Promise<ImagesUpdateResponse>;
  /**
   * Deletes an Image.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param imageName The name of the image.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    imageName: string,
    options?: ImagesDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * Deletes an Image.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param imageName The name of the image.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    imageName: string,
    options?: ImagesDeleteOptionalParams,
  ): Promise<void>;
}
